import { ChangeDetectionStrategy, Component, computed, inject, resource } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  EventqDataTable,
  EventqDataTableColumn,
  EventqLinedTab,
  EventqSortState,
} from '@ecam/eventq-components';
import { environment } from '../../../../../environments/environment';
import { BrowserTabService } from '../../../../core/services/browser-tab.service';
import { AgentDataSyncService } from '../../../../core/services/agent-data-sync.service';
import { EventHandleService } from '../../../../core/services/event-handle.service';
import { autoReloadResource } from '../../../../core/utils/auto-reload-resource';
import { withPreviousResourceValue } from '../../../../core/utils/with-previous-resource-value';
import { activeQueueSort, queueTableColumn } from '../../queue-table-sort';
import { EventsDashboardService } from '../../services/events-dashboard.service';

@Component({
  selector: 'app-events-in-process-section',
  imports: [EventqLinedTab, EventqDataTable],
  templateUrl: './events-in-process-section.html',
  styleUrl: './events-in-process-section.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventsInProcessSection {
  private readonly eventsService = inject(EventsDashboardService);
  private readonly browserTabs = inject(BrowserTabService);
  private readonly eventHandles = inject(EventHandleService);
  private readonly dataSync = inject(AgentDataSyncService);

  readonly sort = computed(() => activeQueueSort(this.browserTabs.inProcessSort()));

  readonly columns: EventqDataTableColumn[] = [
    queueTableColumn({ key: 'firstAlarmDetected', label: 'First Alarm Detected', grow: 1.2 }),
    queueTableColumn({ key: 'location', label: 'Location/Event', grow: 1.6 }),
    queueTableColumn({ key: 'priority', label: 'Priority', grow: 0.7 }),
    queueTableColumn({ key: 'alarmCount', label: 'Alarm Count', grow: 0.7 }),
    queueTableColumn({ key: 'agent', label: 'Agent', grow: 1 }),
  ];

  private readonly inProcessSource = resource({
    params: () => ({
      sort: this.sort(),
      revision: this.eventHandles.handlesRevision(),
      sync: this.dataSync.revisionFor('in-process'),
    }),
    loader: ({ params }) =>
      firstValueFrom(this.eventsService.getEventsInProcess(params.sort ?? undefined)),
  });

  readonly inProcess = withPreviousResourceValue(this.inProcessSource);
  readonly totalCount = computed(() => this.inProcess.value()?.totalCount ?? 0);

  constructor() {
    this.dataSync.watchWhileAlive('in-process');
    autoReloadResource(this.inProcessSource, environment.eventRefreshIntervalMs);
  }

  onSort(sort: EventqSortState): void {
    const next = activeQueueSort(sort);
    if (!next) {
      return;
    }
    this.browserTabs.updateActivePageState({ inProcessSort: next });
  }
}
